/**
 * URL routing (N3, docs/DEEP-LINKS-ROUTER.md) — TanStack Router, code-based routes.
 *
 * The URL is the source of truth. Every route carries the legacy page id in
 * `staticData.page`, so App/AppRouter keep rendering by page id and the page
 * components are untouched; record ids, admin tabs and the sign-in return path
 * live in the URL (params/search). Guards run in `beforeLoad`, before anything
 * renders, using the auth source passed in router context.
 */
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  type RouterHistory,
  type RouteComponent,
} from '@tanstack/react-router';
import { canAccessArea, homePathFor, safeRedirect, type AdminArea, type AuthState } from './auth';

export type FormMode = 'order' | 'quote';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /** Legacy page id rendered by AppRouter ('home' resolves to dashboard or login). */
    page?: string;
    /** Order-form mode for the order/quote routes. */
    formMode?: FormMode;
    /** Admin area for the admin shells (tab comes from the URL). */
    adminArea?: AdminArea;
  }
}

export interface RouterContext {
  auth: AuthState;
}

type GuardArgs = { context: RouterContext; location: { href: string } };

/** Signed-out visitors go to /login and come back here afterwards. */
function requireUser({ context, location }: GuardArgs): void {
  if (!context.auth.getUser()) {
    throw redirect({ to: '/login', search: { redirect: location.href } });
  }
}

/** Wrong role → that user's own home (never a loop: home is always allowed for the role). */
function requireArea(area: AdminArea | 'admin') {
  return (args: GuardArgs): void => {
    requireUser(args);
    const user = args.context.auth.getUser();
    if (!canAccessArea(user, area)) {
      throw redirect({ href: homePathFor(user), replace: true });
    }
  };
}

export function buildRouteTree(rootComponent: RouteComponent) {
  const rootRoute = createRootRouteWithContext<RouterContext>()({ component: rootComponent });

  const route = (path: string, staticData: Record<string, unknown>, extra: Record<string, unknown> = {}) =>
    createRoute({ getParentRoute: () => rootRoute, path, staticData, ...extra } as never);

  // ── Public / entry ──
  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    staticData: { page: 'home' },
    beforeLoad: ({ context }) => {
      const user = context.auth.getUser();
      const home = homePathFor(user);
      if (user && home !== '/') {throw redirect({ href: home, replace: true });}
    },
  });

  const loginSearch = (search: Record<string, unknown>): { redirect?: string } =>
    (typeof search.redirect === 'string' ? { redirect: search.redirect } : {});
  const loginBeforeLoad = ({ context, search }: { context: RouterContext; search: { redirect?: string } }) => {
    const user = context.auth.getUser();
    if (user) {throw redirect({ href: safeRedirect(search.redirect) ?? homePathFor(user), replace: true });}
  };
  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    staticData: { page: 'login' },
    validateSearch: loginSearch,
    beforeLoad: loginBeforeLoad,
  });
  // Cross-subdomain auth transfer lands on /{slug}/login?auth_transfer=1
  const slugLoginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/$slug/login',
    staticData: { page: 'login' },
    validateSearch: loginSearch,
    beforeLoad: loginBeforeLoad,
  });

  const dashboardAlias = createRoute({
    getParentRoute: () => rootRoute,
    path: '/dashboard',
    beforeLoad: () => { throw redirect({ to: '/', replace: true }); },
  });
  // Forgot-password emails use /reset-password?token=…; same page as /set-password. Keep the query.
  const resetPasswordAlias = createRoute({
    getParentRoute: () => rootRoute,
    path: '/reset-password',
    beforeLoad: ({ location }) => {
      throw redirect({ href: `/set-password${location.searchStr ?? ''}${location.hash ? `#${location.hash}` : ''}`, replace: true });
    },
  });
  // Router step 6: links the Worker emails (workers/src/shared/appPaths.ts). Never remove one; alias it.
  // /tickets/{id}: ticket emails. Customers go to /support, admins to their area's Support tab; the ticket opens.
  const ticketAlias = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tickets/$ticketId',
    beforeLoad: (args: GuardArgs & { params: { ticketId: string } }) => {
      requireUser(args);
      const home = homePathFor(args.context.auth.getUser());
      const q = `?ticket=${encodeURIComponent(args.params.ticketId)}`;
      throw redirect({ href: home === '/' ? `/support${q}` : `${home}/support${q}`, replace: true });
    },
  } as never);
  // /billing: old trial email and Stripe return URLs. Admins go to their Billing tab (query kept); others home.
  const billingAlias = createRoute({
    getParentRoute: () => rootRoute,
    path: '/billing',
    beforeLoad: (args: GuardArgs & { location: { searchStr?: string } }) => {
      requireUser(args);
      const home = homePathFor(args.context.auth.getUser());
      throw redirect({ href: home === '/' || home === '/rsladm' ? home : `${home}/billing${args.location.searchStr ?? ''}`, replace: true });
    },
  } as never);
  const publicRoutes = [
    route('/docs/$', { page: 'docs-setup' }), // also matches /docs (empty splat)
    // UX-GROWTH H2: help centre. Public like /docs, so the sign-in page can link to it.
    route('/help', { page: 'help' }),
    route('/help/$article', { page: 'help' }),
    route('/downloads', { page: 'downloads' }),
    // Legal documents (privacy policy, terms …) from /api/legal/documents/{slug}. Public, so the
    // cookie banner and sign-up can link to them before anyone signs in.
    route('/legal/$docSlug', { page: 'legal' }),
    route('/access-request', { page: 'access-request' }),
    route('/set-password', { page: 'set-password' }),
    route('/reseller-register', { page: 'reseller-register' }),
    route('/shell-preview', { page: import.meta.env.DEV ? 'shell-preview' : 'not-found' }),
  ];

  // ── Admin shells (tab in the URL; "dashboard" is the bare path) ──
  const adminRoutes = (['glbadm', 'rsladm', 'tntadm'] as const).flatMap((area) => [
    route(`/${area}`, { page: area, adminArea: area }, { beforeLoad: requireArea(area) }),
    route(`/${area}/$tab`, { page: area, adminArea: area }, { beforeLoad: requireArea(area) }),
    // ADMIN-REVIEW X1: record detail pages (/tntadm/users/{id})
    route(`/${area}/$tab/$recordId`, { page: area, adminArea: area }, { beforeLoad: requireArea(area) }),
  ]);
  const legacyAdminRoute = import.meta.env.DEV
    ? route('/admin', { page: 'admin' }, { beforeLoad: requireArea('admin') })
    : createRoute({
      getParentRoute: () => rootRoute,
      path: '/admin',
      beforeLoad: ({ context }) => { throw redirect({ href: homePathFor(context.auth.getUser()), replace: true }); },
    });

  // ── Customer pages under /{company-slug}/… ──
  const authed = { beforeLoad: requireUser };
  const invoiceSearch = (search: Record<string, unknown>): { order?: string } =>
    (typeof search.order === 'string' && search.order ? { order: search.order } : {});
  const customerRoutes = [
    route('/$slug', { page: 'home' }, authed),
    route('/$slug/dashboard', { page: 'home' }, authed),
    route('/$slug/orders/new', { page: 'order-form', formMode: 'order' }, authed),
    route('/$slug/orders/$orderNo', { page: 'order-form', formMode: 'order' }, authed),
    route('/$slug/quotes/new', { page: 'order-form', formMode: 'quote' }, authed),
    route('/$slug/quotes/$quoteNo', { page: 'order-form', formMode: 'quote' }, authed),
    route('/$slug/invoices/$invoiceNo', { page: 'order-form', formMode: 'order' }, { ...authed, validateSearch: invoiceSearch }),
    route('/$slug/credit-memos/$creditMemoNo', { page: 'credit-memo-detail' }, authed),
    route('/$slug/shipments/$shipmentNo', { page: 'shipment-detail' }, authed),
    route('/$slug/settings', { page: 'settings' }, authed),
    route('/$slug/my-account', { page: 'my-account' }, authed),
    route('/$slug/support', { page: 'support-tickets' }, authed),
    // Slug-free support (target of the /tickets/{id} email alias; the company comes from the session)
    route('/support', { page: 'support-tickets' }, authed),
    // X3: slug-free "New order" (target of the installed app's shortcut; the company comes from the session)
    route('/orders/new', { page: 'order-form', formMode: 'order' }, authed),
  ];
  // Old URLs written by the pre-router replaceState sync (no record id in them).
  const legacyCustomerAliases = [
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/$slug/order-form',
      beforeLoad: ({ params }) => { throw redirect({ to: '/$slug/orders/new', params: { slug: params.slug }, replace: true }); },
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/$slug/credit-memo',
      beforeLoad: ({ params }) => { throw redirect({ to: '/$slug', params: { slug: params.slug }, replace: true }); },
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: '/$slug/shipment',
      beforeLoad: ({ params }) => { throw redirect({ to: '/$slug', params: { slug: params.slug }, replace: true }); },
    }),
  ];

  const notFoundRoute = route('/$', { page: 'not-found' });

  return rootRoute.addChildren([
    indexRoute,
    loginRoute,
    slugLoginRoute,
    dashboardAlias,
    resetPasswordAlias,
    ticketAlias,
    billingAlias,
    ...publicRoutes,
    ...adminRoutes,
    legacyAdminRoute,
    ...customerRoutes,
    ...legacyCustomerAliases,
    notFoundRoute,
  ] as never[]);
}

export function createAppRouter(opts: { rootComponent: RouteComponent; auth: AuthState; history?: RouterHistory }) {
  return createRouter({
    routeTree: buildRouteTree(opts.rootComponent),
    context: { auth: opts.auth },
    history: opts.history,
    defaultPreload: false,
    scrollRestoration: true,
  });
}

export type AppRouterInstance = ReturnType<typeof createAppRouter>;
